import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, AlertTriangle, XCircle, Info, Sparkles } from 'lucide-react';
import { ToastContext, type ToastType } from './ToastContext';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
}

const typeConfig: Record<ToastType, { border: string; icon: typeof CheckCircle; autoDismiss: number | null }> = {
  success:  { border: 'border-l-success',  icon: CheckCircle,   autoDismiss: 4000 },
  error:    { border: 'border-l-error',    icon: XCircle,       autoDismiss: null },
  warning:  { border: 'border-l-warning',  icon: AlertTriangle, autoDismiss: 6000 },
  info:     { border: 'border-l-info',     icon: Info,          autoDismiss: 4000 },
  insight:  { border: 'border-l-gold-500', icon: Sparkles,      autoDismiss: 4000 },
};

const iconColors: Record<ToastType, string> = {
  success: 'text-success',
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
  insight: 'text-gold-500',
};

let toastId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = String(++toastId);
    setToasts((prev) => [...prev.slice(-2), { id, type, message }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-sm:top-auto max-sm:bottom-4 max-sm:right-4 max-sm:left-4">
        <AnimatePresence>
          {toasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  const config = typeConfig[toast.type];
  const Icon = config.icon;

  useEffect(() => {
    if (config.autoDismiss) {
      const timer = setTimeout(() => onDismiss(toast.id), config.autoDismiss);
      return () => clearTimeout(timer);
    }
  }, [toast.id, config.autoDismiss, onDismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 80, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.95 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className={`
        flex items-start gap-3 px-4 py-3 rounded-lg min-w-[280px] max-w-[380px]
        bg-dark-surface border-l-[3px] ${config.border}
        text-sm text-content font-body shadow-card
        ${toast.type === 'insight' ? 'shadow-glow-gold' : ''}
      `}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${iconColors[toast.type]}`} />
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 text-content-muted hover:text-content transition-colors cursor-pointer"
      >
        <X size={14} />
      </button>
    </motion.div>
  );
}
